import { useEffect, useMemo, useState } from 'react'
import {
  omit,
  assign,
  cloneDeep,
  get,
  groupBy,
  unset,
  isEmpty,
} from 'lodash-es'
import { createContainer } from 'unstated-next'
import { useToasts } from 'react-toast-notifications'
import clientContainer from '~/lib/client'
import log from '../../../lib/log'
import QueryStringProvider from '../../../hooks/useQueryString'
import {
  FieldValidationError,
  GlobalValidationError,
  ITaskTemplate,
  ITemplateError,
  IValidationDetailError,
  NodeValidationError,
} from '../types'
import { useMutation } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'

interface IExistingTemplateMetadata {
  name: string
  discoveryTemplate: boolean
  variables: IVariable[]
  taskDefaults: Object
  tags: Record<string, string>
  description: string
  iconURL: string
}
export interface IVariable {
  name: string
  id?: string
  displayName?: string
  mandatory?: boolean
  value?: any
  defaultValue?: any
  type?: string
  description?: string
  format?: any
  overwriteByStream?: boolean
  overwriteByData?: boolean
}

interface ITemplateMetadata {
  name: string
  discoveryTemplate: boolean
  variables: IVariable[]
  tags: Record<string, string>
  description: string
  iconURL: string
}

interface ICreatedResponse {
  entity: ITaskTemplate
}

function useTemplate() {
  const [existingTemplateMetadata, setExistingTemplateMetadata] =
    useState<IExistingTemplateMetadata>(null)
  const { query, setQueryValue } = QueryStringProvider.useContainer()
  const [shouldRefreshDebug, setShouldRefreshDebug] = useState(false)
  const validationProblems = usevalidationProblems()
  const [hasBeenValidated, setHasBeenValidated] = useState(false)
  const [taskDefaults, setTaskDefaults] = useState({})
  const [variables, setVariables] = useState<IVariable[]>([])
  const [variablesValidation, setVariablesValidation] = useState({})
  const [templateMetadata, setTemplateMetadata] =
    useState<ITemplateMetadata>(null)

  const { addToast } = useToasts()

  const save = useMutation({
    mutationFn: async (arg: { template: any }) => {
      const { template } = arg
      validationProblems.clear()
      await clientContainer.client.templates
        .create(template, { failOnWarning: false })
        .then((response: ICreatedResponse) => {
          setQueryValue('template', response.entity.name)
          addToast('Successfully saved template', { appearance: 'success' })
        })
    },
    onError: (error: ITemplateError) => {
      log.error({ err: error })
      const message = get(error, ['response', 'data', 'error'], error.message)
      return addToast(
        <span>
          Failed to save template: <br /> {message}
        </span>,
        { appearance: 'error', autoDismiss: true },
      )
    },
    throwOnError: false,
  })

  const update = useMutation({
    mutationFn: async (arg: {
      template: { name: string; [key: string]: any }
    }) => {
      const { template } = arg
      validationProblems.clear()

      await clientContainer.client.templates
        .update(template.name, template, {
          failOnWarning: false,
        })
        .then(res => {
          const template: any = res
          setExistingTemplateMetadata({
            name: template.name,
            discoveryTemplate: template.discoveryTemplate,
            variables: template.variables,
            taskDefaults: template.taskDefaults,
            tags: template.tags,
            description: template.description,
            iconURL: template.iconURL,
          })
          addToast('Successfully updated template', { appearance: 'success' })
        })
    },
    onError: (error: ITemplateError) => {
      const message = get(error, ['response', 'data', 'error'], error.message)
      log.error({ err: error })
      return addToast(
        <span>
          Failed to update template: <br /> {message}
        </span>,
        { appearance: 'error', autoDismiss: true },
      )
    },
    throwOnError: false,
  })

  const _parseTemplateMetadata = metadata => {
    const { tags = {} } = metadata

    if (!Array.isArray(tags)) return metadata

    const definedTags = tags.filter(({ key }) => key) // filter out empty tag keys. ('foo': '') is permitted
    const keyValueEntries = definedTags.map(({ key, value }) => [key, value])

    return { ...metadata, tags: Object.fromEntries(keyValueEntries) }
  }

  const _parseToTemplate = graph => {
    return {
      ...graph,
      ..._parseTemplateMetadata(templateMetadata),
      variables: variables.map((value: any) => omit(value, ['value', 'id'])),
      taskDefaults: _parseTemplateMetadata(taskDefaults),
    }
  }

  const saveTemplate = (name, newName, graph) => {
    if (!graph) return
    const parsedGraphDefinition = _parseToTemplate({
      ...graph,
      name: newName,
    })
    return name === newName
      ? update.mutate({ template: parsedGraphDefinition })
      : save.mutate({ template: parsedGraphDefinition })
  }

  useEffect(() => {
    if (variables.length === 0) return

    const unnamedVariables = variables.filter(variable =>
      isEmpty(variable.name),
    )
    if (unnamedVariables.length > 0) {
      const validations = {}
      unnamedVariables.forEach(variable => {
        validations[variable.id] = {
          name: 'Variable name is required',
        }
      })
      setVariablesValidation(validations)
    } else {
      setVariablesValidation({})
    }
  }, [variables])

  useEffect(() => {
    if (!existingTemplateMetadata) return
    const { taskDefaults = {}, ...meta } = existingTemplateMetadata

    const newVariables = existingTemplateMetadata?.variables
      ? existingTemplateMetadata?.variables?.map(variable => {
          return {
            ...variable,
            id: uuid(),
          }
        })
      : []

    setVariables(newVariables)
    setTaskDefaults({ ...taskDefaults })
    setTemplateMetadata({ ...meta })
  }, [existingTemplateMetadata])

  const refreshTemplateValues = () => {
    if (!existingTemplateMetadata) return
    const { taskDefaults = {}, ...meta } = existingTemplateMetadata

    const newVariables = existingTemplateMetadata?.variables
      ? existingTemplateMetadata?.variables?.map(variable => {
          return {
            ...variable,
            id: uuid(),
          }
        })
      : []

    setVariables(newVariables)

    setTaskDefaults({ ...taskDefaults })
    setTemplateMetadata({ ...meta })
  }

  const updateVariable = (variable: IVariable) => {
    const { id } = variable
    setVariables(prevState => {
      const newState = prevState.map(v => {
        if (v.id === id) {
          return variable
        }
        return v
      })

      return newState
    })
  }

  const removeVariable = (variableId: string) => {
    setVariables(prevState => {
      const newState = prevState.filter(v => v.id !== variableId)
      return newState
    })
  }

  const updateDebugVariable = variable => {
    const { value, name } = variable
    setVariables(prevState => {
      const newState = prevState.map(v => {
        if (v.name === name) {
          if (value === '' || (typeof value === 'number' && isNaN(value))) {
            return v
          } else {
            return {
              ...v,
              value,
            }
          }
        }
        return v
      })
      return newState
    })
  }

  const addVariable = () => {
    const newVariable: IVariable = {
      name: '',
      defaultValue: '',
      mandatory: true,
      displayName: '',
      type: 'string',
      description: '',
      value: '',
      id: uuid(),
      overwriteByStream: false,
      overwriteByData: false,
    }
    setVariables(prevState => [...prevState, newVariable])
  }

  const templateTitle = decodeURIComponent(query.template ?? '')

  const isLoading = save.isPending || update.isPending

  return {
    save,
    update,
    isLoading,

    existingTemplateMetadata,
    setExistingTemplateMetadata,
    templateTitle,
    variables,
    updateVariable,
    removeVariable,
    addVariable,
    variablesValidation,
    updateDebugVariable,

    ...validationProblems, // contains all validation stuff
    hasBeenValidated,
    setHasBeenValidated,

    taskDefaults,
    setTaskDefaults,

    templateMetadata,
    setTemplateMetadata,
    refreshTemplateValues,
    shouldRefreshDebug,
    setShouldRefreshDebug,
    saveTemplate,
  } as const
}

function usevalidationProblems() {
  const [globalValidationProblems, _setGlobalValidationProblems] = useState<
    GlobalValidationError[]
  >([])
  const [nodeValidationProblems, _setNodeValidationProblems] = useState<
    Record<string, NodeValidationError[]>
  >({})
  const [fieldValidationProblems, _setFieldValidationProblems] = useState<
    Record<string, FieldValidationError[]>
  >({})

  const globalValidationCount = useMemo(
    () => globalValidationProblems?.length,
    [globalValidationProblems],
  )

  const nodeValidationCount = useMemo(
    () => Object.values(nodeValidationProblems).flat().length,
    [nodeValidationProblems],
  )

  const fieldValidationCount = useMemo(
    () => Object.values(fieldValidationProblems).flat().length,
    [fieldValidationProblems],
  )

  const setGlobalValidationProblems = (errors: IValidationDetailError[]) => {
    const globalProblems = errors.filter(
      err => !err.details || Boolean(err.details.cycle),
    )
    _setGlobalValidationProblems(globalProblems)
  }

  const setNodeValidationProblems = (errors: IValidationDetailError[]) => {
    const nodeProblems = errors.filter(
      err =>
        Boolean(err.details) &&
        Boolean(err.details.nodeId) &&
        !err.details.property,
    )
    const nodeErrorRecord = groupBy(nodeProblems, err =>
      err.details.actuatorLabel
        ? err.details.actuatorLabel
        : err.details.nodeId,
    )

    _setNodeValidationProblems(nodeErrorRecord)
  }

  const setFieldValidationProblems = (errors: IValidationDetailError[]) => {
    const fieldProblems = errors.filter(
      err =>
        Boolean(err.details) &&
        Boolean(err.details.nodeId) &&
        Boolean(err.details.property),
    )
    const nodeFieldErrorRecord = groupBy(fieldProblems, err =>
      err.details.actuatorLabel
        ? err.details.actuatorLabel
        : err.details.nodeId,
    )

    _setFieldValidationProblems(nodeFieldErrorRecord)
  }

  const setValidationProblems = (errors: IValidationDetailError[]) => {
    setGlobalValidationProblems(errors)
    setNodeValidationProblems(errors)
    setFieldValidationProblems(errors)
  }

  const clear = () => {
    _setGlobalValidationProblems([])
    _setNodeValidationProblems({})
    _setFieldValidationProblems({})
  }

  const removeNodeErrors = (nodeName: string) => {
    _setNodeValidationProblems(prev => {
      const newNodeProblems = cloneDeep(prev)
      unset(newNodeProblems, nodeName)
      return newNodeProblems
    })

    _setFieldValidationProblems(prev => {
      const newNodeProblems = cloneDeep(prev)
      unset(newNodeProblems, nodeName)
      return newNodeProblems
    })
  }

  const clearFieldError = (nodeName: string, fieldName: string) => {
    _setFieldValidationProblems(prev => {
      const errors = prev[nodeName]
      if (!errors) {
        return prev
      }

      const newErrors = errors.filter(
        err => err.details.property.toLowerCase() !== fieldName.toLowerCase(),
      )
      return assign({}, prev, { [nodeName]: newErrors })
    })
  }

  return {
    globalValidationProblems,
    fieldValidationProblems,
    nodeValidationProblems,
    validationErrorCount:
      globalValidationCount + fieldValidationCount + nodeValidationCount,
    globalValidationCount,
    fieldValidationCount,
    nodeValidationCount,

    setValidationProblems,
    clear,
    clearFieldError,
    removeNodeErrors,
  } as const
}

const container = createContainer(useTemplate)
export default container
